
import React, { useEffect, useState } from 'react';
import { User } from '../types';
import { Save, User as UserIcon, CheckCircle, Lock } from 'lucide-react';
// API 통합관리
import { API_BASE_URL } from '../config';

// const API_BASE_URL = 'http://localhost:8000';

interface SettingsProps {
  currentUser: User;
  onUpdateUser: (user: User) => Promise<void> | void;
  onLogout: () => void;
  globalRefreshKey?: number;
}

const Settings: React.FC<SettingsProps> = ({ currentUser, onUpdateUser, onLogout, globalRefreshKey = 0 }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');

  // Profile State
  const [profileForm, setProfileForm] = useState<User>(currentUser);
  const [isProfileSaved, setIsProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [nextPasswordConfirm, setNextPasswordConfirm] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    setProfileForm(currentUser);
  }, [currentUser]);

  const handleSaveProfile = async () => {
    setProfileError(null);
    try {
      await onUpdateUser({ ...profileForm });
      setIsProfileSaved(true);
      setTimeout(() => setIsProfileSaved(false), 2000);
    } catch (e: any) {
      setProfileError(e?.message || '저장 중 오류가 발생했습니다.');
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    if (!currentPassword || !nextPassword || !nextPasswordConfirm) {
      setPasswordError('현재 비밀번호와 새 비밀번호를 모두 입력하세요.');
      return;
    }
    if (nextPassword !== nextPasswordConfirm) {
      setPasswordError('새 비밀번호 확인이 일치하지 않습니다.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: profileForm.username || currentUser.username,
          current_password: currentPassword,
          new_password: nextPassword,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || '비밀번호 변경 실패');
      }

      setPasswordSaved(true);
      setCurrentPassword('');
      setNextPassword('');
      setNextPasswordConfirm('');
      setTimeout(() => setPasswordSaved(false), 2000);
    } catch (e: any) {
      setPasswordError(e?.message || '비밀번호 변경 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col animate-fade-in bg-[#f8fafc] pb-20">
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="w-full lg:w-64 flex flex-col gap-2">
          <button onClick={() => setActiveTab('profile')} className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium text-left ${activeTab === 'profile' ? 'bg-white text-blue-600 shadow-sm border border-blue-100' : 'text-slate-500 hover:bg-slate-100'}`}>
            <UserIcon size={18} /> 내 정보 수정
          </button>
          <button onClick={() => setActiveTab('password')} className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium text-left ${activeTab === 'password' ? 'bg-white text-blue-600 shadow-sm border border-blue-100' : 'text-slate-500 hover:bg-slate-100'}`}>
            <Lock size={18} /> 비밀번호 변경
          </button>
        </div>

        <div className="flex-1">
          {activeTab === 'profile' && (
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 max-w-2xl">
              <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                <UserIcon size={20} className="text-blue-500"/> 프로필 설정
              </h3>
              <div className="space-y-6">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">이름</label>
                    <input type="text" value={profileForm.name} onChange={(e) => setProfileForm({...profileForm, name: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-100 outline-none" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">연락처</label>
                      <input type="text" value={profileForm.phone} onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-100 outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">이메일</label>
                      <input type="email" value={profileForm.email} onChange={(e) => setProfileForm({...profileForm, email: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-100 outline-none" />
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-100 flex items-center gap-4">
                  <button onClick={handleSaveProfile} className="bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center gap-2">
                    <Save size={18} /> 저장하기
                  </button>
                  <button onClick={onLogout} className="bg-white text-slate-700 px-6 py-3 rounded-xl font-bold border border-slate-200 hover:bg-slate-50 transition-colors">
                    로그아웃
                  </button>
                  {isProfileSaved && <span className="text-green-600 font-bold flex items-center gap-1 animate-fade-in"><CheckCircle size={18} /> 저장되었습니다.</span>}
                  {profileError && <span className="text-red-600 font-bold text-sm">{profileError}</span>}
                </div>
              </div>
            </div>
          )}
          {activeTab === 'password' && (
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 max-w-2xl">
              <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                <Lock size={20} className="text-blue-500" /> 비밀번호 변경
              </h3>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">현재 비밀번호</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">새 비밀번호</label>
                    <input
                      type="password"
                      value={nextPassword}
                      onChange={(e) => setNextPassword(e.target.value)}
                      className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">새 비밀번호 확인</label>
                    <input
                      type="password"
                      value={nextPasswordConfirm}
                      onChange={(e) => setNextPasswordConfirm(e.target.value)}
                      className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleChangePassword}
                    className="bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors"
                  >
                    비밀번호 변경
                  </button>
                  {passwordSaved && <span className="text-green-600 font-bold flex items-center gap-1 animate-fade-in"><CheckCircle size={18} /> 변경되었습니다.</span>}
                  {passwordError && <span className="text-red-600 font-bold text-sm">{passwordError}</span>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
